import { Injectable, OnInit, Injector } from '@angular/core';
import { environment } from '../../environments/environment';
import { AuthService } from '../core/auth/auth.service';
import { AcuStorageService } from './acustorage.service';
import { ApiService } from './api.service';
import { DataSyncService } from './data-sync.service';

@Injectable({
  providedIn: 'root'
})
export class GoogleAuthService implements OnInit {

  constructor(
    public authService: AuthService,
    private acuStorageService: AcuStorageService,
    private injector: Injector
  ) { }

  public gapiSetup: boolean = false; // marks if the gapi library has been loaded
  public authInstance: gapi.auth2.GoogleAuth;
  public error: string;
  public user: gapi.auth2.GoogleUser;
  private initPromise: Promise<void> | null = null;

  async ngOnInit() {
    console.log('initGoogleAuth ngOnInit');
    try {
      if (await this.checkIfUserAuthenticated()) {
        this.user = this.authInstance?.currentUser?.get();
      }
    } catch (e) {
      console.warn('[GoogleAuthService] Error checking authentication in ngOnInit:', e);
    }
  }

  /**
   * Helper to safely poll for window.gapi availability (avoids ReferenceError if script is loading or blocked)
   */
  private async waitForGapi(timeoutMs: number = 3000): Promise<any> {
    if (typeof window === 'undefined') {
      return null;
    }
    const win = window as any;
    if (win.gapi) {
      return win.gapi;
    }

    const startTime = Date.now();
    return new Promise((resolve) => {
      const interval = setInterval(() => {
        if (win.gapi) {
          clearInterval(interval);
          resolve(win.gapi);
        } else if (Date.now() - startTime >= timeoutMs) {
          clearInterval(interval);
          resolve(null);
        }
      }, 100);
    });
  }

  async initGoogleAuth(): Promise<void> {
    console.log('initGoogleAuth');
    if (this.gapiSetup && this.authInstance) {
      return;
    }
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = (async () => {
      try {
        const gapiObj = await this.waitForGapi(3000);
        if (!gapiObj) {
          console.warn('[GoogleAuthService] Google API script (gapi) not available or blocked by client.');
          return;
        }

        await new Promise<void>((resolve) => {
          try {
            gapiObj.load('auth2', async () => {
              try {
                if (gapiObj.auth2) {
                  const auth = await gapiObj.auth2.init({ client_id: environment.googleProviderID });
                  this.gapiSetup = true;
                  this.authInstance = auth;
                } else {
                  console.warn('[GoogleAuthService] gapi.auth2 undefined after load');
                }
              } catch (initErr) {
                console.warn('[GoogleAuthService] gapi.auth2.init failed:', initErr);
              } finally {
                resolve();
              }
            });
          } catch (loadErr) {
            console.warn('[GoogleAuthService] gapi.load threw an error:', loadErr);
            resolve();
          }
        });
      } catch (err) {
        console.warn('[GoogleAuthService] Unexpected error in initGoogleAuth:', err);
      } finally {
        if (!this.gapiSetup) {
          this.initPromise = null; // allow retry on next attempt
        }
      }
    })();

    return this.initPromise;
  }

  async authenticate(): Promise<GoogleUser> {
    console.log('authenticate');

    // Initialize gapi if not done yet
    if (!this.gapiSetup) {
      await this.initGoogleAuth();
    }

    if (!this.authInstance) {
      throw new Error('Google Sign-In non disponibile al momento. Verifica la connessione o disabilita eventuali estensioni adblocker.');
    }

    // Resolve or reject signin Promise
    let gUser: GoogleUser;
    try {
      const user = await this.authInstance.signIn({ prompt: 'select_account' });
      console.log('login avvenuto!');
      console.log('basicProfile => ' + JSON.stringify(user.getBasicProfile()));
      console.log('getAuthResponse => ' + JSON.stringify(user.getAuthResponse()));
      console.log('getId => ' + JSON.stringify(user.getId()));
      console.log('getGrantedScopes => ' + JSON.stringify(user.getGrantedScopes()));

      console.log('JWT TOKEN => ' + user.getAuthResponse().id_token);

      gUser = {
        email: user.getBasicProfile().getEmail(),
        photoUrl: user.getBasicProfile().getImageUrl(),
        firstName: user.getBasicProfile().getGivenName(),
        lastName: user.getBasicProfile().getFamilyName(),
        id: user.getBasicProfile().getId(),
        name: user.getBasicProfile().getName(),
      };
      
      const apiService = this.injector.get(ApiService);
      await apiService.loginSessionBridge(user.getAuthResponse().id_token);
      this.acuStorageService.setJwtToken(user.getAuthResponse().id_token);

      this.authService.login(gUser);
      return gUser;
    } catch (error) {
      console.log('login error ' + JSON.stringify(error));
      throw error;
    }
  }

  async logout(): Promise<gapi.auth2.GoogleUser | void> {
    console.log('logout');
    // Initialize gapi if not done yet
    if (!this.gapiSetup) {
      await this.initGoogleAuth();
    }

    if (!this.authInstance) {
      console.warn('[GoogleAuthService] logout called but authInstance is not available');
      return;
    }

    try {
      await this.authInstance.signOut();
      console.log('logout avvenuto!');
    } catch (error) {
      console.log('logout error ' + error);
      this.error = String(error);
    }
  }

  async checkIfUserAuthenticated(): Promise<boolean> {
    // Initialize gapi if not done yet
    if (!this.gapiSetup) {
      await this.initGoogleAuth();
    }

    if (!this.authInstance) {
      return false;
    }

    return this.authInstance.isSignedIn.get();
  }

  async refreshToken(): Promise<string> {
    console.log('refreshToken chiamato');
    if (!this.gapiSetup) {
      await this.initGoogleAuth();
    }
    if (this.authInstance && this.authInstance.isSignedIn.get()) {
      const currentUser = this.authInstance.currentUser.get();
      if (currentUser) {
        try {
          const authResponse = await currentUser.reloadAuthResponse();
          const newJwtToken = authResponse.id_token;
          const apiService = this.injector.get(ApiService);
          await apiService.loginSessionBridge(newJwtToken);
          this.acuStorageService.setJwtToken(newJwtToken);
          console.log('Token aggiornato con successo');
          try {
            const dataSync = this.injector.get(DataSyncService);
            void dataSync.syncAll('token-refreshed', true);
          } catch (e) {
            // Service not yet initialized
          }
          return newJwtToken;
        } catch (error) {
          console.error('Errore durante il refresh del token Google:', error);
          throw error;
        }
      }
    }
    throw new Error('Utente non autenticato o istanza Google non pronta');
  }

}

export interface GoogleUser {
  id: string;
  email: string;
  name: string;
  photoUrl: string;
  firstName: string;
  lastName: string;
}
